import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GenreMultiSelect } from '@/components/ui/GenreMultiSelect'
import { Select } from '@/components/ui/Select'

export interface BookFilters {
  search: string
  genres: string[]
  statuses: string[]
  ratingRange: { min: number; max: number }
  sortBy: string
  sortOrder: 'asc' | 'desc'
  /** Only books finished (end_date) in this year */
  readYear?: number | null
}

interface FilterPanelProps {
  filters: BookFilters
  onFiltersChange: (filters: BookFilters) => void
  className?: string
  compact?: boolean
  hideStatusFilter?: boolean
  hideRatingFilter?: boolean
}

const SORT_OPTIONS = [
  { value: 'title', label: 'books.fields.title' },
  { value: 'author', label: 'books.fields.author' },
  { value: 'created_at', label: 'common.dateAdded' },
  { value: 'rating', label: 'books.fields.rating' },
  { value: 'status', label: 'books.fields.status' },
] as const

const STATUS_OPTIONS = [
  { value: 'unread', label: 'books.status.unread' },
  { value: 'reading', label: 'books.status.reading' },
  { value: 'read', label: 'books.status.read' },
  { value: 'wishlist', label: 'books.status.wishlist' },
] as const

export function FilterPanel({
  filters,
  onFiltersChange,
  className = '',
  compact = false,
  hideStatusFilter = false,
  hideRatingFilter = false,
}: FilterPanelProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const updateFilters = (updates: Partial<BookFilters>) => {
    onFiltersChange({ ...filters, ...updates })
  }

  const clearAllFilters = () => {
    onFiltersChange({
      search: '',
      genres: [],
      statuses: hideStatusFilter ? filters.statuses : [], // Preserve current statuses if hidden
      ratingRange: { min: 0, max: 5 },
      sortBy: 'created_at',
      sortOrder: 'desc',
      readYear: null,
    })
  }

  const hasActiveFilters =
    filters.search !== '' ||
    filters.genres.length > 0 ||
    (!hideStatusFilter && filters.statuses.length > 0) ||
    (!hideRatingFilter && (filters.ratingRange.min > 0 || filters.ratingRange.max < 5)) ||
    !!filters.readYear

  const activeFiltersCount = [
    !!filters.readYear,
    filters.search !== '',
    filters.genres.length > 0,
    !hideStatusFilter && filters.statuses.length > 0,
    !hideRatingFilter && (filters.ratingRange.min > 0 || filters.ratingRange.max < 5),
  ].filter(Boolean).length

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsExpanded(false)
      }
    }

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExpanded])

  // Compact mode: show only filter button
  if (compact) {
    return (
      <div className={`relative flex items-center gap-2 ${className}`} ref={dropdownRef}>
        {filters.readYear && (
          <button
            type="button"
            onClick={() => updateFilters({ readYear: null })}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
            title={t('books.filters.clearReadYear')}
          >
            {t('books.filters.readInYear', { year: filters.readYear })}
            <span aria-hidden="true">×</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all duration-200 font-medium text-sm min-w-[120px]
            ${
              hasActiveFilters
                ? 'bg-primary/5 border-primary/20 text-primary hover:bg-primary/10 shadow-sm'
                : 'bg-card border-border text-foreground hover:bg-muted hover:border-border'
            }
          `}
          title={t('common.filter')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span>{t('common.filter')}</span>
          {activeFiltersCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-primary-foreground bg-primary rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </button>

        {/* Filter Dropdown */}
        {isExpanded && (
          <>
            {/* Mobile overlay */}
            <button
              type="button"
              className="fixed inset-0 bg-popover/50 z-40 sm:hidden"
              onClick={() => setIsExpanded(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsExpanded(false)
                }
              }}
              aria-label="Close filters"
            />

            {/* Filter modal */}
            <div className="fixed inset-x-4 top-4 bottom-4 bg-card rounded-xl shadow-xl z-50 overflow-hidden sm:absolute sm:inset-auto sm:top-full sm:right-0 sm:mt-2 sm:w-96 sm:max-w-none">
              <div className="p-4 bg-muted/30 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">{t('common.filter')}</h3>
                  <div className="flex items-center gap-2">
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                      >
                        {t('common.clearAll')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsExpanded(false)}
                      className="sm:hidden p-2 hover:bg-muted rounded-lg transition-colors"
                      aria-label="Close filters"
                    >
                      <svg
                        className="w-5 h-5 text-muted-foreground"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-6 flex-1 overflow-y-auto sm:max-h-96">
                {/* Genres Filter */}
                <div className="space-y-3">
                  <div className="text-sm font-medium text-foreground">
                    {t('books.fields.genres')}
                  </div>
                  <GenreMultiSelect
                    selectedGenres={filters.genres}
                    onGenresChange={(genreIds) => updateFilters({ genres: genreIds })}
                  />
                </div>

                {/* Status Filter */}
                {!hideStatusFilter && (
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-foreground">
                      {t('books.fields.status')}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((status) => {
                        const isSelected = filters.statuses.includes(status.value)
                        return (
                          <button
                            type="button"
                            key={status.value}
                            onClick={() => {
                              const newStatuses = isSelected
                                ? filters.statuses.filter((s) => s !== status.value)
                                : [...filters.statuses, status.value]
                              updateFilters({ statuses: newStatuses })
                            }}
                            className={`px-3 py-2 rounded-full text-xs font-medium border transition-all duration-200 ${
                              isSelected
                                ? 'bg-primary/10 text-primary border-primary/20 shadow-sm'
                                : 'bg-muted/50 text-foreground border-border hover:bg-muted hover:border-border'
                            }`}
                          >
                            {t(status.label)}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Rating Range Filter */}
                {!hideRatingFilter && (
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-foreground">
                      {t('books.fields.rating')}
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <label
                            htmlFor="rating-min-desktop"
                            className="text-xs text-muted-foreground mb-1 block"
                          >
                            {t('common.minimum')}
                          </label>
                          <input
                            id="rating-min-desktop"
                            type="range"
                            min="0"
                            max="5"
                            step="1"
                            value={filters.ratingRange.min}
                            onChange={(e) =>
                              updateFilters({
                                ratingRange: {
                                  ...filters.ratingRange,
                                  min: Number(e.target.value),
                                },
                              })
                            }
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>0</span>
                            <span className="font-medium text-primary">
                              {filters.ratingRange.min}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1">
                          <label
                            htmlFor="rating-max-desktop"
                            className="text-xs text-muted-foreground mb-1 block"
                          >
                            {t('common.maximum')}
                          </label>
                          <input
                            id="rating-max-desktop"
                            type="range"
                            min="0"
                            max="5"
                            step="1"
                            value={filters.ratingRange.max}
                            onChange={(e) =>
                              updateFilters({
                                ratingRange: {
                                  ...filters.ratingRange,
                                  max: Number(e.target.value),
                                },
                              })
                            }
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span className="font-medium text-primary">
                              {filters.ratingRange.max}
                            </span>
                            <span>5</span>
                          </div>
                        </div>
                      </div>

                      {/* Star Rating Visual */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                        <span className="text-xs font-medium">
                          {filters.ratingRange.min === filters.ratingRange.max
                            ? t('common.exactlyStars', { stars: filters.ratingRange.min })
                            : t('common.rangeStars', {
                                min: filters.ratingRange.min,
                                max: filters.ratingRange.max,
                              })}
                        </span>
                        <div className="flex ml-auto">
                          {[1, 2, 3, 4, 5].map((starNum) => (
                            <svg
                              key={`rating-star-desktop-${starNum}`}
                              className={`w-4 h-4 ${
                                starNum >= filters.ratingRange.min &&
                                starNum <= filters.ratingRange.max
                                  ? 'text-amber'
                                  : 'text-muted'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sort Options */}
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-900">{t('common.sort')}</div>
                  <div className="flex gap-3">
                    <Select
                      aria-label={t('common.sort')}
                      value={filters.sortBy}
                      onChange={(sortBy) => updateFilters({ sortBy })}
                      className="flex-1"
                      options={SORT_OPTIONS.map((option) => ({
                        value: option.value,
                        label: t(option.label),
                      }))}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        updateFilters({
                          sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc',
                        })
                      }
                      className="px-3 py-2 border border-input rounded-lg hover:bg-muted transition-colors"
                      title={
                        filters.sortOrder === 'asc' ? t('common.ascending') : t('common.descending')
                      }
                    >
                      <svg
                        className={`w-4 h-4 text-muted-foreground transition-transform ${
                          filters.sortOrder === 'desc' ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Mobile Apply Button */}
                <div className="sm:hidden p-4 border-t border-border bg-muted/30">
                  <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t('common.apply')} {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  // Full mode (original)
  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm ${className}`}>
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-foreground">{t('common.filter')}</h3>
          {activeFiltersCount > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {activeFiltersCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('common.clearAll')}
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Filter Content - Full Mode */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Same content as compact mode but without dropdown wrapper */}
          {/* Genres Filter */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-foreground">{t('books.fields.genres')}</div>
            <GenreMultiSelect
              selectedGenres={filters.genres}
              onGenresChange={(genreIds) => updateFilters({ genres: genreIds })}
            />
          </div>

          {/* Status Filter */}
          {!hideStatusFilter && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-foreground">{t('books.fields.status')}</div>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((status) => {
                  const isSelected = filters.statuses.includes(status.value)
                  return (
                    <button
                      type="button"
                      key={status.value}
                      onClick={() => {
                        const newStatuses = isSelected
                          ? filters.statuses.filter((s) => s !== status.value)
                          : [...filters.statuses, status.value]
                        updateFilters({ statuses: newStatuses })
                      }}
                      className={`px-3 py-2 rounded-full text-xs font-medium border transition-colors ${
                        isSelected
                          ? 'bg-primary/10 text-primary border-primary/20'
                          : 'bg-muted/50 text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {t(status.label)}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Rating Range Filter */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-foreground">{t('books.fields.rating')}</div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label htmlFor="rating-min-mobile" className="text-xs text-muted-foreground">
                    {t('common.minimum')}
                  </label>
                  <input
                    id="rating-min-mobile"
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={filters.ratingRange.min}
                    onChange={(e) =>
                      updateFilters({
                        ratingRange: { ...filters.ratingRange, min: Number(e.target.value) },
                      })
                    }
                    className="mt-1 w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0</span>
                    <span className="font-medium">{filters.ratingRange.min}</span>
                  </div>
                </div>

                <div className="flex-1">
                  <label htmlFor="rating-max-mobile" className="text-xs text-muted-foreground">
                    {t('common.maximum')}
                  </label>
                  <input
                    id="rating-max-mobile"
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={filters.ratingRange.max}
                    onChange={(e) =>
                      updateFilters({
                        ratingRange: { ...filters.ratingRange, max: Number(e.target.value) },
                      })
                    }
                    className="mt-1 w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span className="font-medium">{filters.ratingRange.max}</span>
                    <span>5</span>
                  </div>
                </div>
              </div>

              {/* Star Rating Visual */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {filters.ratingRange.min === filters.ratingRange.max
                    ? t('common.exactlyStars', { stars: filters.ratingRange.min })
                    : t('common.rangeStars', {
                        min: filters.ratingRange.min,
                        max: filters.ratingRange.max,
                      })}
                </span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((starNum) => (
                    <svg
                      key={`rating-star-mobile-${starNum}`}
                      className={`w-4 h-4 ${
                        starNum >= filters.ratingRange.min && starNum <= filters.ratingRange.max
                          ? 'text-amber'
                          : 'text-muted'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sort Options */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-foreground">{t('common.sort')}</div>
            <div className="flex gap-3">
              <Select
                aria-label={t('common.sort')}
                value={filters.sortBy}
                onChange={(sortBy) => updateFilters({ sortBy })}
                className="flex-1"
                options={SORT_OPTIONS.map((option) => ({
                  value: option.value,
                  label: t(option.label),
                }))}
              />

              <button
                type="button"
                onClick={() =>
                  updateFilters({
                    sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc',
                  })
                }
                className="px-3 py-2 border border-input rounded-lg hover:bg-muted transition-colors"
                title={filters.sortOrder === 'asc' ? t('common.ascending') : t('common.descending')}
              >
                <svg
                  className={`w-4 h-4 text-muted-foreground transition-transform ${
                    filters.sortOrder === 'desc' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
